import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, Paper, TextField, MenuItem, Button, Chip, Divider, Stack, Card, CardHeader, CardContent, IconButton, Tabs, Tab, Snackbar, Alert, Select, InputLabel, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { fetchJson, resolveApiUrl } from '../../../utils/fetchJson';
import useAyPhaseReady from '../../../utils/useAyPhaseReady';
import usePhaseClasses from '../../../hooks/usePhaseClasses';
import { usePhase } from '../../../contexts/PhaseContext';
import { DataGrid } from '@mui/x-data-grid';
import AppLoader from '../../../components/AppLoader';

const SubjectHubPage = () => {
  const [subjects, setSubjects] = useState([]);
  const { t } = useTranslation();
  const theme = useTheme();
  const [teachers, setTeachers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' });
  const { ready } = useAyPhaseReady();
  const { classes: phaseClasses } = usePhaseClasses({ allowEmptyFallback: false });
  const { activePhase } = usePhase();

  // Form state
  const [subjectForm, setSubjectForm] = useState({ name: '', coefficient: 1, categoryId: '' });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [categoryForms, setCategoryForms] = useState([{ id: `cat-${Date.now()}`, name: '', description: '' }]); // at least one default form
  const [assignForm, setAssignForm] = useState({ classId: '', subjectId: '', teacherId: '', periods: 0 });
  const [loading, setLoading] = useState(true);
  const [editSubject, setEditSubject] = useState(null);
  const [editCategory, setEditCategory] = useState(null);
  // Batch add / review list
  const [pendingSubjects, setPendingSubjects] = useState([]); // legacy staging list (not used in new UI)
  const [subjectForms, setSubjectForms] = useState([{ id: `sub-${Date.now()}`, name: '', coefficient: 1, categoryId: '' }]); // at least one default form
  const [bulkAssign, setBulkAssign] = useState({ classId: '', subjectIds: [], teacherId: '', periods: 0 });
  const [teacherAlloc, setTeacherAlloc] = useState({ teacherId: '', subjectIds: [] });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const maxGuard = setTimeout(() => setLoading(false), 2000);

      // Build no-flash cache keys (phase-only for metadata)
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}`;
      const cacheKeySubjects = `subjectHub:subjects:${ctxKey}`;
      const cacheKeyCategories = `subjectHub:categories:${ctxKey}`;

      // Prefill from cache to avoid flash
      let prefilled = false;
      try {
        const rawSub = localStorage.getItem(cacheKeySubjects);
        if (rawSub) {
          const cached = JSON.parse(rawSub);
          if (Array.isArray(cached)) setSubjects(cached);
          if (Array.isArray(cached) && cached.length) prefilled = true;
        }
      } catch(_) {}
      try {
        const rawCat = localStorage.getItem(cacheKeyCategories);
        if (rawCat) {
          const cached = JSON.parse(rawCat);
          if (Array.isArray(cached)) setCategories(cached);
          if (Array.isArray(cached) && cached.length) prefilled = true;
        }
      } catch(_) {}

      // If we have cached content, show it immediately
      if (prefilled) setLoading(false);

      try {
        // Load subjects and categories from API namespace (phase-scoped by headers)
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId } : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
        const [subj, cats] = await Promise.all([
          fetchJson('/api/subjects', { headers }),
          fetchJson('/api/subject-categories', { headers }),
        ]);
        const subList = Array.isArray(subj) ? subj : (subj?.subjects || []);
        const catList = Array.isArray(cats) ? cats : (cats?.categories || []);
        setSubjects(subList);
        setCategories(catList);
        // Update cache
        try { localStorage.setItem(cacheKeySubjects, JSON.stringify(subList)); } catch(_) {}
        try { localStorage.setItem(cacheKeyCategories, JSON.stringify(catList)); } catch(_) {}
      } catch (e) {
        console.error('Failed loading subjects/categories', e);
      }
      try {
        // Teachers and grades are best-effort; do not block UI if missing
        const [tchs] = await Promise.all([
          fetchJson('/api/staff/faculty').catch(() => []),
        ]);
        setTeachers(Array.isArray(tchs) ? tchs : (tchs?.teachers || []));
      } catch {}
      try {
        // Use strict phase classes from hook instead of fetching /classes
        setGrades(phaseClasses || []);
      } catch {}
      clearTimeout(maxGuard);
      setLoading(false);
    };
    if (ready) loadData();
  }, [ready, activePhase, phaseClasses]);

  const notify = (severity, msg) => setSnack({ open: true, severity, msg });

  const saveSubject = async () => {
    try {
      const payload = { name: subjectForm.name, coefficient: Number(subjectForm.coefficient) || 1, categoryId: subjectForm.categoryId || null };
      const created = await fetchJson('/api/subjects', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      setSubjects(prev => [created, ...prev]);
      setSubjectForm({ name: '', coefficient: 1, categoryId: '' });
      notify('success', t('academics.subjectHub.messages.subjectCreated'));
    } catch (e) {
      notify('error', e.message);
    }
  };

  const saveCategory = async () => {
    try {
      const created = await fetchJson('/api/subject-categories', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(categoryForm) });
      setCategories(prev => [created, ...prev]);
      setCategoryForm({ name: '', description: '' });
      notify('success', t('academics.subjectHub.messages.categoryCreated'));
    } catch (e) { notify('error', e.message); }
  };

  const updateSubject = async () => {
    if (!editSubject) return;
    try {
      const payload = { name: editSubject.name, coefficient: Number(editSubject.coefficient) || 1, categoryId: editSubject.categoryId || null };
      const updated = await fetchJson(`/api/subjects/${editSubject.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      setSubjects(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
      setEditSubject(null);
      notify('success', t('academics.subjectHub.messages.subjectUpdated'));
    } catch (e) { notify('error', e.message); }
  };

  const deleteSubject = async (id) => {
    try {
      await fetchJson(`/api/subjects/${id}`, { method: 'DELETE' });
      setSubjects(prev => prev.filter(s => s.id !== id));
      notify('success', t('academics.subjectHub.messages.subjectDeleted'));
    } catch (e) { notify('error', e.message); }
  };

  const updateCategory = async () => {
    if (!editCategory) return;
    try {
      const updated = await fetchJson(`/api/subject-categories/${editCategory.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: editCategory.name, description: editCategory.description }) });
      setCategories(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
      setEditCategory(null);
      notify('success', t('academics.subjectHub.messages.categoryUpdated'));
    } catch (e) { notify('error', e.message); }
  };

  const deleteCategory = async (id) => {
    try {
      await fetchJson(`/api/subject-categories/${id}`, { method: 'DELETE' });
      setCategories(prev => prev.filter(c => c.id !== id));
      notify('success', t('academics.subjectHub.messages.categoryDeleted'));
    } catch (e) { notify('error', e.message); }
  };

  // Data for tables
  const categoryColumns = useMemo(() => ([
    { field: 'name', headerName: t('academics.subjectHub.table.headers.name'), flex: 1 },
    { field: 'description', headerName: t('academics.subjectHub.table.headers.description'), flex: 1 },
    {
      field: 'actions', headerName: t('academics.subjectHub.table.headers.actions'), width: 120, sortable: false, filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => setEditCategory({ id: params.row.id, name: params.row.name, description: params.row.description })}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => deleteCategory(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      )
    },
  ]), [t]);

  const categoryRows = useMemo(() => (categories || []).map(c => ({ id: c.id, name: c.name, description: c.description || '' })), [categories]);

  const subjectColumns = useMemo(() => [
    { field: 'name', headerName: t('academics.subjectHub.table.headers.name'), flex: 1 },
    { field: 'coefficient', headerName: t('academics.subjectHub.table.headers.coefficient'), width: 130 },
    { field: 'categoryName', headerName: t('academics.subjectHub.table.headers.category'), flex: 1 },
    {
      field: 'actions', headerName: t('academics.subjectHub.table.headers.actions'), width: 120, sortable: false, filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => setEditSubject({ id: params.row.id, name: params.row.name, coefficient: params.row.coefficient, categoryId: params.row.categoryId })}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => deleteSubject(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      )
    }
  ], [t]);

  const subjectRows = useMemo(() => (subjects || []).map(s => ({
    id: s.id,
    name: s.name,
    coefficient: s.coefficient,
    categoryId: s.categoryId || '',
    categoryName: (categories || []).find(c => c.id === s.categoryId)?.name || ''
  })), [subjects, categories]);

  const assignSubject = async () => {
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = schoolId ? { 'Content-Type': 'application/json', 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId } : { 'Content-Type': 'application/json', 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
      const payload = { classId: assignForm.classId, subjectId: assignForm.subjectId, teacherId: assignForm.teacherId, academicYearId: ayId, phaseId: phaseId ? Number(phaseId) : undefined };
      await fetchJson(`/api/class-subjects?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`, { method: 'POST', headers, body: JSON.stringify(payload) });
      notify('success', t('academics.subjectHub.messages.assignedSuccessfully'));
    } catch (e) { notify('error', e.message); }
  };

  const downloadTemplate = async () => {
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const headers = {};
      if (token) headers['Authorization'] = token.toLowerCase().startsWith('bearer ') ? token : `Bearer ${token}`;
      if (schoolId) headers['X-Scope-School-Id'] = schoolId;
      if (phaseCode) headers['X-Active-Phase'] = phaseCode;
      if (phaseId) headers['X-Active-Phase-Id'] = phaseId;
      if (ayId) headers['X-Academic-Year-Id'] = ayId;
      const res = await fetch(resolveApiUrl('/api/subjects/template'), { headers, credentials: 'include' });
      if (!res.ok) throw new Error(`Template download failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subject_import_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { notify('error', e.message); }
  };

  const importSubjects = async (file) => {
    try {
      // Try preview import so user can review and save
      const form = new FormData();
      form.append('file', file);
      const data = await fetchJson('/subjects/import?preview=1', { method: 'POST', body: form });
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      if (!items.length) throw new Error(t('academics.subjectHub.errors.noSubjectsParsed'));
      // Map into subjectForms so each imported subject becomes its own form
      const temp = items.map((it, idx) => ({ id: `imp-${Date.now()}-${idx}`, name: it.name || '', coefficient: Number(it.coefficient) || 1, categoryId: it.categoryId || '' }));
      setSubjectForms(prev => [...temp, ...prev.filter(f => f.name || f.coefficient || f.categoryId)]);
      notify('success', t('academics.subjectHub.messages.loadedIntoForms', { count: temp.length }));
    } catch (e) { notify('error', e.message); }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) importSubjects(f);
    e.target.value = '';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <AppLoader
          variant="circular3"
          size={58}
          messages={[
            t('academics.subjectHub.loading.subjects'),
            t('academics.subjectHub.loading.categories'),
            t('academics.subjectHub.loading.scopingByPhase')
          ]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Hero */}
      <Box sx={{
        bgcolor: 'primary.main',
        color: theme.palette.getContrastText(theme.palette.primary.main),
        borderRadius: 4,
        p: 2,
        mb: 2,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h3" fontWeight={800} gutterBottom>
          {t('academics.subjectHub.hero.title')}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          {t('academics.subjectHub.hero.subtitle')}
        </Typography>
        <Stack direction="row" gap={2} sx={{ mt: 2 }}>
          <Button variant="outlined" color="inherit" startIcon={<UploadFileIcon />} onClick={downloadTemplate}>{t('academics.subjectHub.actions.downloadTemplate')}</Button>
          <Button component="label" variant="outlined" color="inherit" startIcon={<UploadFileIcon />}>{t('academics.subjectHub.actions.importCsvExcel')}
            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={onFilePick} />
          </Button>
        </Stack>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('academics.subjectHub.tabs.categories')} />
        <Tab label={t('academics.subjectHub.tabs.createSubjects')} />
        <Tab label={t('academics.subjectHub.tabs.teacherAllocations')} />
        <Tab label={t('academics.subjectHub.tabs.classAllocation')} />
      </Tabs>

      <Grid container spacing={3}>
        {/* Categories first */}
        {tab === 0 && (
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader title={t('academics.subjectHub.cards.subjectCategories.title')} subheader={t('academics.subjectHub.cards.subjectCategories.subheader')} />
            <CardContent>
              {/* Multi-form add for categories */}
              <Stack direction={{ xs:'column', sm:'row' }} spacing={1} sx={{ mb: 1 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={()=> setCategoryForms(list=> ([{ id:`cat-${Date.now()}`, name:'', description:'' }, ...list]))}>{t('academics.subjectHub.actions.addCategoryForm')}</Button>
                <Button variant="contained" disabled={!categoryForms.length} onClick={async()=>{
                  try{
                    for(const cf of categoryForms){
                      if(!cf.name) continue;
                      const created = await fetchJson('/api/subject-categories', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ name: cf.name, description: cf.description||'' })});
                      setCategories(prev=> [created, ...prev]);
                    }
                    setCategoryForms([{ id: `cat-${Date.now()}`, name: '', description: '' }]);
                    notify('success', t('academics.subjectHub.messages.savedAllCategories'));
                  }catch(e){ notify('error', e.message); }
                }}>{t('academics.subjectHub.actions.saveAll')}</Button>
                <Button color="error" disabled={!categoryForms.length} onClick={()=> setCategoryForms([{ id: `cat-${Date.now()}`, name: '', description: '' }])}>{t('common.clear')}</Button>
              </Stack>
              <Grid container spacing={2}>
                {(categoryForms||[]).map((cf)=> (
                  <Grid item key={cf.id} xs={12}>
                    <Paper sx={{ p:2 }} variant="outlined">
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label={t('academics.subjectHub.fields.categoryName')} value={cf.name} onChange={(e)=> setCategoryForms(list=> list.map(item=> item.id===cf.id? { ...item, name:e.target.value } : item))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label={t('academics.subjectHub.fields.description')} value={cf.description||''} onChange={(e)=> setCategoryForms(list=> list.map(item=> item.id===cf.id? { ...item, description:e.target.value } : item))} />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <div style={{ height: 420, width: '100%' }}>
                <DataGrid rows={categoryRows} columns={categoryColumns} pageSizeOptions={[5,10,20]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
              </div>
            </CardContent>
          </Card>
        </Grid>
        )}

        {tab === 1 && (
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader title={t('academics.subjectHub.cards.createSubjects.title')} subheader={t('academics.subjectHub.cards.createSubjects.subheader')} />
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setSubjectForms(list => ([{ id: `sub-${Date.now()}`, name: '', coefficient: 1, categoryId: '' }, ...list]))}>{t('academics.subjectHub.actions.addSubjectForm')}</Button>
                <Button variant="outlined" startIcon={<UploadFileIcon />} component="label">{t('academics.subjectHub.actions.import')}
                  <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={onFilePick} />
                </Button>
                <Button variant="contained" disabled={!subjectForms.length} onClick={async ()=>{
                  try{
                    for(const item of subjectForms){
                      const payload = { name: item.name, coefficient: Number(item.coefficient)||1, categoryId: item.categoryId||null };
                      if(!payload.name) continue;
                      const created = await fetchJson('/api/subjects', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload)});
                      setSubjects(prev => [created, ...prev]);
                    }
                    setSubjectForms([{ id: `sub-${Date.now()}`, name: '', coefficient: 1, categoryId: '' }]);
                    notify('success', t('academics.subjectHub.messages.savedAllSubjects'));
                  }catch(e){ notify('error', e.message); }
                }}>{t('academics.subjectHub.actions.saveAll')}</Button>
                <Button color="error" disabled={!subjectForms.length} onClick={()=> setSubjectForms([{ id: `sub-${Date.now()}`, name: '', coefficient: 1, categoryId: '' }])}>{t('common.clear')}</Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('academics.subjectHub.helper.templateInstructions')}
              </Typography>
              <Grid container spacing={2}>
                {(subjectForms||[]).map(sf => (
                  <Grid item key={sf.id} xs={12}>
                    <Paper sx={{ p:2 }} variant="outlined">
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={5}>
                          <TextField fullWidth label={t('academics.subjectHub.fields.subjectName')} value={sf.name} onChange={(e)=> setSubjectForms(list=> list.map(it=> it.id===sf.id? { ...it, name:e.target.value } : it))} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField type="number" fullWidth label={t('academics.subjectHub.fields.coefficient')} value={sf.coefficient} onChange={(e)=> setSubjectForms(list=> list.map(it=> it.id===sf.id? { ...it, coefficient:e.target.value } : it))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel id={`cat-${sf.id}`}>{t('academics.subjectHub.fields.category')}</InputLabel>
                            <Select labelId={`cat-${sf.id}`} label={t('academics.subjectHub.fields.category')} value={sf.categoryId||''} onChange={(e)=> setSubjectForms(list=> list.map(it=> it.id===sf.id? { ...it, categoryId:e.target.value } : it))}>
                              <MenuItem value=""><em>{t('academics.subjectHub.labels.none')}</em></MenuItem>
                              {(categories||[]).map(c=> <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}

        {tab === 1 && (
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader title={t('academics.subjectHub.cards.subjects.title')} subheader={t('academics.subjectHub.cards.subjects.subheader')} />
            <CardContent>
              <div style={{ height: 420, width: '100%' }}>
                <DataGrid rows={subjectRows} columns={subjectColumns} pageSizeOptions={[5,10,20]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
              </div>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Teacher Allocations */}
        {tab === 2 && (
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader title={t('academics.subjectHub.cards.teacherAllocations.title')} subheader={t('academics.subjectHub.cards.teacherAllocations.subheader')} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label={t('academics.subjectHub.fields.teacher')} value={teacherAlloc.teacherId} onChange={(e)=> setTeacherAlloc(s=> ({ ...s, teacherId: e.target.value }))}>
                    {(teachers||[]).map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.name || `${t.firstName} ${t.lastName}`}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('academics.subjectHub.labels.subjects')}</Typography>
                  <Grid container spacing={1}>
                    {(subjects||[]).map(su => (
                      <Grid key={su.id} item xs={12} sm={6} md={4}>
                        <Chip
                          variant={teacherAlloc.subjectIds.includes(su.id)? 'filled':'outlined'}
                          color={teacherAlloc.subjectIds.includes(su.id)? 'primary':'default'}
                          label={su.name}
                          onClick={()=> setTeacherAlloc(state=> ({ ...state, subjectIds: state.subjectIds.includes(su.id)? state.subjectIds.filter(id=> id!==su.id) : [...state.subjectIds, su.id] }))}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs:'column', sm:'row' }} spacing={1}>
                    <Button variant="contained" disabled={!teacherAlloc.teacherId || !(teacherAlloc.subjectIds||[]).length} onClick={async()=>{
                      try{
                        // Persist allocations (assumes backend endpoint exists)
                        for(const sid of teacherAlloc.subjectIds){
                          await fetchJson('/api/teacher-subjects', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ teacherId: teacherAlloc.teacherId, subjectId: sid })});
                        }
                        notify('success', t('academics.subjectHub.messages.teacherAllocationsSaved'));
                      }catch(e){ notify('error', e.message); }
                    }}>{t('academics.subjectHub.actions.saveAllocations')}</Button>
                    <Button onClick={()=> setTeacherAlloc({ teacherId:'', subjectIds:[] })}>{t('academics.subjectHub.actions.reset')}</Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Class Allocation */}
        {tab === 3 && (
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader title={t('academics.subjectHub.cards.classAllocation.title')} subheader={t('academics.subjectHub.cards.classAllocation.subheader')} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label={t('academics.subjectHub.fields.class')} value={bulkAssign.classId} onChange={(e)=> setBulkAssign(s=>({...s, classId:e.target.value}))}>
                    {(phaseClasses||grades||[]).map(g=> (<MenuItem key={g.id} value={g.id}>{g.name||g.label||g.className||t('academics.subjectHub.labels.classWithId', { id: g.id })}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('academics.subjectHub.labels.subjects')}</Typography>
                  <Grid container spacing={1}>
                    {(subjects||[]).map(su => (
                      <Grid key={su.id} item xs={12} sm={6} md={4}>
                        <Chip
                          variant={bulkAssign.subjectIds.includes(su.id)? 'filled':'outlined'}
                          color={bulkAssign.subjectIds.includes(su.id)? 'primary':'default'}
                          label={su.name}
                          onClick={()=> setBulkAssign(state=> ({ ...state, subjectIds: state.subjectIds.includes(su.id)? state.subjectIds.filter(id=> id!==su.id) : [...state.subjectIds, su.id] }))}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* Removed teacher/periods from Class Allocation per requirement */}
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs:'column', sm:'row' }} spacing={1}>
                    <Button variant="contained" disabled={!bulkAssign.classId || !(bulkAssign.subjectIds||[]).length} onClick={async()=>{
                      try{
                        for(const sid of (bulkAssign.subjectIds||[])){
                          const payload = { classId: bulkAssign.classId, subjectId: sid };
                          await fetchJson('/api/class-subjects', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload)});
                        }
                        notify('success', t('academics.subjectHub.messages.bulkAssignmentComplete'));
                      }catch(e){ notify('error', e.message); }
                    }}>{t('academics.subjectHub.actions.assignSelected')}</Button>
                    <Button onClick={()=> setBulkAssign({ classId:'', subjectIds:[], teacherId:'', periods:0 })}>{t('academics.subjectHub.actions.reset')}</Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}

        

        {/* Resource Attachments removed per requirement */}
      </Grid>

      {/* Loading state handled by AppLoader at top */}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Edit Subject Dialog */}
      <Dialog open={!!editSubject} onClose={() => setEditSubject(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('academics.subjectHub.dialogs.editSubjectTitle')}</DialogTitle>
        <DialogContent>
          {editSubject && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('academics.subjectHub.fields.name')} value={editSubject.name} onChange={e => setEditSubject(s => ({ ...s, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField type="number" fullWidth label={t('academics.subjectHub.fields.coefficient')} value={editSubject.coefficient} onChange={e => setEditSubject(s => ({ ...s, coefficient: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="cat-edit-label">{t('academics.subjectHub.fields.category')}</InputLabel>
                  <Select labelId="cat-edit-label" label={t('academics.subjectHub.fields.category')} value={editSubject.categoryId || ''} onChange={e => setEditSubject(s => ({ ...s, categoryId: e.target.value }))}>
                    <MenuItem value=""><em>{t('academics.subjectHub.labels.none')}</em></MenuItem>
                    {(categories||[]).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSubject(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={updateSubject}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={!!editCategory} onClose={() => setEditCategory(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('academics.subjectHub.dialogs.editCategoryTitle')}</DialogTitle>
        <DialogContent>
          {editCategory && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField fullWidth label={t('academics.subjectHub.fields.name')} value={editCategory.name} onChange={e => setEditCategory(s => ({ ...s, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label={t('academics.subjectHub.fields.description')} value={editCategory.description || ''} onChange={e => setEditCategory(s => ({ ...s, description: e.target.value }))} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCategory(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={updateCategory}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectHubPage;
